import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Check, Zap, Shield, Users, TerminalSquare, ChartLine } from "lucide-react";

export default function Pro() {
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [packages, setPackages] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/pro/packages").then(({data}) => setPackages(data.packages)).catch(()=>{});
  }, []);

  const upgrade = async (packageId) => {
    if (!user) { nav("/login"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/pro/checkout", {
        package_id: packageId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Checkout failed");
      setBusy(false);
    }
  };

  const isPro = user?.pro_active;

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1200px] mx-auto px-6 py-14">
        <div className="text-center mb-14">
          <div className="chip chip-accent mx-auto inline-flex" data-testid="pro-eyebrow"><Zap size={12}/> MQL4FORGE · PRO</div>
          <h1 className="font-display text-5xl md:text-6xl text-white tracking-tight mt-4">
            Ship <span className="italic text-[var(--accent)]">production</span> robots.
          </h1>
          <p className="text-lg text-[var(--text-secondary)] mt-4 max-w-2xl mx-auto">
            Compile-check, cloud backtest, and team sharing — the stuff you need before you drop a live EA on real money.
          </p>
          {isPro && (
            <div className="chip chip-profit mx-auto inline-flex mt-6" data-testid="pro-active-badge">
              ◆ PRO ACTIVE · expires {new Date(user.pro_expires_at).toLocaleDateString()}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-14">
          <FeatureBig icon={<TerminalSquare/>} title="Compile-check"
            body="Server-side static analysis of your .mq4. Catches unbalanced braces, missing entry points, dangerous pip math, and typos before you hit F7 in MetaEditor."/>
          <FeatureBig icon={<ChartLine/>} title="Cloud backtest"
            body="Instant historical-style backtest on synthetic candles. Win rate, drawdown, equity curve. Iterate faster than any MT4 tester."/>
          <FeatureBig icon={<Users/>} title="Team sharing"
            body="Share strategies with your co-traders by email. View or edit permissions. Perfect for prop-firm teams and signal groups."/>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {packages.map((p) => (
            <div key={p.id} data-testid={`pro-plan-${p.id}`}
              className={`card-sharp p-8 relative ${p.id === "pro_yearly" ? "border-[var(--accent)]" : ""}`}>
              {p.id === "pro_yearly" && (
                <div className="chip chip-accent absolute -top-3 left-8" data-testid="pro-plan-best-value">◆ BEST VALUE · Save $79</div>
              )}
              <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)]">{p.label}</div>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="font-display text-6xl text-white">${p.amount.toFixed(0)}</span>
                <span className="text-sm text-[var(--text-secondary)]">/{p.duration_days} days</span>
              </div>
              <ul className="mt-6 space-y-2.5 text-sm">
                {[
                  "Unlimited AI generation (Claude / GPT / Gemini)",
                  "Compile-check on every strategy",
                  "Cloud backtest & equity curves",
                  "Team sharing (view / edit)",
                  "Priority AI queue",
                ].map((f) => (
                  <li key={f} className="flex items-start gap-2 text-[var(--text-primary)]">
                    <Check size={16} className="text-[var(--accent)] mt-0.5 shrink-0"/> {f}
                  </li>
                ))}
              </ul>
              <button data-testid={`pro-upgrade-${p.id}-btn`}
                onClick={() => upgrade(p.id)} disabled={busy}
                className={`w-full mt-8 py-3 rounded flex items-center justify-center gap-2 font-semibold transition-all
                  ${p.id === "pro_yearly" ? "btn-primary" : "btn-outline hover:!bg-[var(--accent)] hover:!text-black"}`}>
                {busy ? "Redirecting…" : `Upgrade — $${p.amount.toFixed(0)}`}
              </button>
            </div>
          ))}
        </div>

        <p className="text-xs font-mono text-[var(--text-muted)] text-center mt-10">
          <Shield size={11} className="inline mr-1"/> Secure checkout by Stripe. Test mode: card 4242 4242 4242 4242, any future expiry, any CVC.
        </p>
      </main>
    </div>
  );
}

function FeatureBig({ icon, title, body }) {
  return (
    <div className="card-sharp p-6">
      <div className="h-11 w-11 flex items-center justify-center rounded-sm border border-[var(--accent)]/30 bg-[var(--accent)]/5 text-[var(--accent)]">
        {React.cloneElement(icon, { size: 18 })}
      </div>
      <h3 className="font-display text-xl text-white mt-4">{title}</h3>
      <p className="text-sm text-[var(--text-secondary)] mt-2 leading-relaxed">{body}</p>
    </div>
  );
}
